// The cart builder's "Add email seats" section: sell more seats on a Microsoft
// 365 subscription the account already has.
//
// The line comes from squish-backend (lib/staff-email-change.js
// staffEmailChangeOptions with addSeats, through squish-crm-backend's
// /api/accounts/:accountId/email-subscriptions/:subId/cart-options), priced by
// lib/email-seat-change.js quoteEmailSeats: the added seats' share of what
// Stripe charges for the rest of the current period. This file only picks the
// served line and keeps the cart to what checkout will accept:
//
//   - one seat change per subscription (a new count replaces the old line),
//   - never seats AND a tier upgrade on the same subscription. Checkout
//     refuses that mix: the seats would be priced at the tier the upgrade is
//     replacing.
//
// The price is for the rest of the period, so it shrinks every day, and
// checkout refuses a cart whose price has moved past its stale-cart tolerance
// (the larger of 50 cents and 1%). A link paid days after it was built can be
// refused and need a fresh cart.

const SEATS = 'EMAIL_SEATS';
const UPGRADE = 'EMAIL_UPGRADE';

// The most seats one change may add (squish-backend MAX_SEAT_STEP, which
// refuses more with SEAT_STEP_TOO_LARGE).
export const MAX_SEAT_STEP = 25;

export const isEmailSeatsLine = (i) => i?.productCode === SEATS;

export const emailSeatsLineId = (line) => `email-seats:${line?.metadata?.subscriptionId || ''}`;

// The account's subscriptions this section can add seats to, from its
// products (listAccountProducts). The email adapter lists `manage_seats` only
// on a live subscription; one without it is left out.
export function seatableEmailSubs(products) {
  return (Array.isArray(products) ? products : [])
    .filter((p) => p?.productKind === 'email' && p.productId
      && (p.actions || []).some((a) => a?.kind === 'manage_seats'))
    .map((p) => ({
      subscriptionId: String(p.productId),
      label: p.label || p.sku || String(p.productId),
      domain: p.linkedDomain || null,
    }));
}

// Add the served line. A new seat count on the same subscription replaces the
// line already there, and everything else keeps its order.
export function withEmailSeats(items, line) {
  const lineId = emailSeatsLineId(line);
  return [...(items || []).filter((i) => i.lineId !== lineId), { ...line, lineId }];
}

// Why the rep can't add this line right now (null = addable).
//   subs: null while loading, { error } when the account's products failed,
//         otherwise the array from seatableEmailSubs.
export function emailSeatsBlocker({ isInvoice, isProspect, subs, items, line }) {
  if (isProspect) return 'Seats are added to a subscription the customer already has, so it needs their account';
  if (isInvoice) return 'Card checkout only: switch to Card to sell more seats';
  if (subs == null) return 'Loading this account\'s email subscriptions…';
  if (subs.error) return `Couldn't load this account's email subscriptions: ${subs.error}`;
  if (subs.length === 0) return 'No live email subscription on this account to add seats to.';
  if (!line) return null;
  const sub = line.metadata?.subscriptionId;
  if ((items || []).some((i) => i?.productCode === UPGRADE && i.metadata?.subscriptionId === sub)) {
    return 'This cart already upgrades this subscription. Add the seats on their own cart, after the upgrade.';
  }
  return null;
}

// Why an upgrade can't go in a cart that already adds seats to its
// subscription (null = it can). src/lib/emailChange.js asks this.
export function upgradeBesideSeatsBlocker(items, subscriptionId) {
  return (items || []).some((i) => isEmailSeatsLine(i) && i.metadata?.subscriptionId === subscriptionId)
    ? 'This cart already adds seats to this subscription. Sell the upgrade on its own cart.'
    : null;
}

// Plain-language price for a seats quote: what is due today, and what the
// subscription bills from its next period.
export function seatsSummary(q) {
  if (!q || q.error) return q?.error || 'not available';
  const d = (c) => `$${(c / 100).toFixed(2)}`;
  const per = q.billingTerm === 'annual' ? 'yr' : 'mo';
  const until = q.periodEnd ? ` until ${new Date(q.periodEnd).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' })}` : '';
  const then = Number.isFinite(q.recurringIncreaseCents) && Number.isFinite(q.recurringCents)
    ? `, then +${d(q.recurringIncreaseCents)}/${per} (${d(q.recurringCents)}/${per} in all)`
    : '';
  return `${q.fromSeats} → ${q.seats} users: ${d(q.dueTodayCents)} today for the time left${until}${then}`;
}
